import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  EditOutlined,
} from '@mui/icons-material';
import { Tooltip } from '@sb/cc-design-system';
import { useNavigate } from 'react-router-dom';
import oneHorizontalScreen from 'src/assets/images/location/screens/1_screen_H.svg';
import oneVerticalScreen from 'src/assets/images/location/screens/1_screen_V.svg';
import twoHorizontalScreen from 'src/assets/images/location/screens/2_screen_H.svg';
import twoVerticalScreen from 'src/assets/images/location/screens/2_screen_V.svg';
import threeHorizontalScreen from 'src/assets/images/location/screens/3_screen_H.svg';
import threeVerticalScreen from 'src/assets/images/location/screens/3_screen_V.svg';
import fourHorizontalScreen from 'src/assets/images/location/screens/4_screen_H.svg';
import fourVerticalScreen from 'src/assets/images/location/screens/4_screen_V.svg';
import fiveHorizontalScreen from 'src/assets/images/location/screens/5_screen_H.svg';
import fiveVerticalScreen from 'src/assets/images/location/screens/5_screen_V.svg';
import DriveThru from 'src/assets/images/location/screens/DriveThru.svg';
import { PATHS } from 'src/constants/paths';
import { ILayoutType, ILocationGroup, ScreenSize } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  CHANNEL_CARD_DESIGN,
  ChannelActionIconButton,
  ChannelActions,
  ChannelCard,
  ChannelFooter,
  ChannelName,
  ChannelNameWrapper,
  ChannelPreview,
  ChannelPreviewImage,
} from './ChannelOO.styled';

interface ChannelProps {
  channelName: string;
  locationCode: string;
  locationGroup: ILocationGroup | undefined;
  navigateToLocationGroupEdit: (id?: number | undefined) => void;
  layoutType?: ILayoutType;
  screens?: ScreenSize[];
}

const getStaticChannelImage = ({
  channelName,
  screens = [],
}: {
  channelName: string;
  screens?: ScreenSize[];
}) => {
  if (screens.length === 0) return oneVerticalScreen;

  const horizontalCount = screens.filter(
    (screen) => screen.width >= screen.height,
  ).length;
  const verticalCount = screens.length - horizontalCount;
  const dominantOrientation = horizontalCount > verticalCount ? 'H' : 'V';
  const screenCount =
    dominantOrientation === 'H' ? horizontalCount : verticalCount;

  const screenAssetMap: Record<string, string> = {
    '1_H': oneHorizontalScreen,
    '1_V': oneVerticalScreen,
    '2_H': twoHorizontalScreen,
    '2_V': twoVerticalScreen,
    '3_H': threeHorizontalScreen,
    '3_V': threeVerticalScreen,
    '4_H': fourHorizontalScreen,
    '4_V': fourVerticalScreen,
    '5_H': fiveHorizontalScreen,
    '5_V': fiveVerticalScreen,
  };
  if (channelName === 'Drive Thru' && screens.length === 3) return DriveThru;

  return (
    screenAssetMap[`${screenCount}_${dominantOrientation}`] ?? oneVerticalScreen
  );
};

export const ChannelOO = ({
  channelName,
  locationCode,
  locationGroup,
  screens,
}: ChannelProps) => {
  const navigate = useNavigate();
  const hasGroup = Boolean(locationGroup && !locationGroup.is_default);
  const staticChannelImage = getStaticChannelImage({ channelName, screens });

  return (
    <ChannelCard design={CHANNEL_CARD_DESIGN}>
      <ChannelPreview>
        <ChannelPreviewImage
          src={staticChannelImage}
          alt={`${channelName} screen preview`}
        />
      </ChannelPreview>
      <ChannelFooter>
        <ChannelNameWrapper>
          <Tooltip title={channelName} placement="top" showOnOverflowOnly>
            <ChannelName>{channelName}</ChannelName>
          </Tooltip>
        </ChannelNameWrapper>

        <ChannelActions>
          <Tooltip
            title={hasGroup ? lang.calendar : 'No location group assigned'}>
            <span>
              <ChannelActionIconButton
                size="small"
                aria-label={lang.calendar}
                disabled={!hasGroup}
                onClick={() =>
                  navigate({
                    pathname: PATHS.LOCATION_CALENDAR(locationCode),
                    search: locationGroup?.id
                      ? `groupID=${locationGroup.id}`
                      : undefined,
                  })
                }>
                <CalendarMonthOutlined fontSize="inherit" />
              </ChannelActionIconButton>
            </span>
          </Tooltip>
          <Tooltip title={lang.time}>
            <ChannelActionIconButton
              size="small"
              aria-label={lang.time}
              onClick={() => {
                const timingPath = PATHS.LOCATION_TIMINGS(
                  locationCode,
                  locationGroup?.id ? String(locationGroup.id) : undefined,
                );

                navigate(timingPath);
              }}>
              <AccessTimeOutlined fontSize="inherit" />
            </ChannelActionIconButton>
          </Tooltip>
          <Tooltip title={hasGroup ? lang.edit : 'No location group assigned'}>
            <span>
              <ChannelActionIconButton
                size="small"
                aria-label={lang.edit}
                disabled={!hasGroup}
                onClick={() => {
                  const menuBoardPath = PATHS.MENU_BOARD_MODIFICATION(
                    locationCode,
                    encodeURIComponent(channelName),
                    locationGroup?.id ? String(locationGroup.id) : undefined,
                  );

                  navigate(menuBoardPath);
                }}>
                <EditOutlined fontSize="inherit" />
              </ChannelActionIconButton>
            </span>
          </Tooltip>
        </ChannelActions>
      </ChannelFooter>
    </ChannelCard>
  );
};
